/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ConfirmEmailComponent } from 'assets/components';
import { verifyEmailScopes } from '../../../utilities/constants';
import Countdown from '../../../utilities/counter';
import { EMAIL_VERIFICATION_SEND, EMAIL_VERIFY_BY_CODE, PARTIAL_SIGN_UP } from '../mutations';
import { showAlert } from '../../../utilities/notifications';
import { sendStats, FLOWS } from '../../../services/mixpanel';
import {
  MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_RESEND,
  MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_SUCCESS,
} from '../mixpanel-events';
import { PARTIAL_SIGN_UP_PHASES, RECAPTCHA_EVENTS } from '../constants';
import { OnboardingContext } from '../../../contexts/onboarding/context';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../utilities/caching';

const emailCountdown = new Countdown();

const ConfirmEmail = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();
  const { state } = useLocation();

  const [verificationId, setVerificationId] = useState(null);

  const [isSubmitRequest, setIsSubmitRequest] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const { state: onboardingState } = useContext(OnboardingContext);
  const allowPartialOnboarding = onboardingState?.mainSettings?.feature_enable_partial_registration;

  const [timer, setTimer] = useState();
  const [form, updateForm] = useState({
    email: state?.email,
    code: '',
    codeScope: verifyEmailScopes.SIGNUP,
  });

  const { mutate: sendEmailVerification, isLoading } = useMutation(EMAIL_VERIFICATION_SEND, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        setVerificationId(data?.verification_id);
        emailCountdown.start();
      }
      sendStats(MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_RESEND, { error }, FLOWS.ONBOARDING);
    },
  });

  const { mutate: partialSignup } = useMutation(PARTIAL_SIGN_UP, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        history.push(`phone-number`, {
          ...state,
          verification_id: verificationId,
          partialSignupData: data,
        });
      }
    },
  });

  const { mutate: verifyEmailByCode, isLoading: isLoadingVerifyEmail } = useMutation(
    EMAIL_VERIFY_BY_CODE,
    {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({
              message: translate('invalid_code_msg_email_verification'),
              type: 'danger',
            });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          const { success, has_account: hasAccount } = data;
          if (success) {
            sendStats(
              MXP_ONBOARDING_SIGN_UP_EMAIL_VERIF_SUCCESS,
              { error, isVerified: success },
              FLOWS.ONBOARDING
            );
          }
          if (success && !hasAccount) {
            if (allowPartialOnboarding) {
              const requestData = {
                phase: PARTIAL_SIGN_UP_PHASES.ACCOUNT,
                email: state?.email,
                password: state?.password,
                country_of_residence: state?.countryOfResidenceId,
                allow_marketing: state?.allowMarketing,
                verification_id: verificationId,
              };
              if (state?.smsOptIn) {
                requestData.is_sms_opt_in = state?.smsOptIn;
              }
              partialSignup(requestData);
            } else {
              history.push(`phone-number`, { ...state, verification_id: verificationId });
            }
          } else if (success && hasAccount) {
            showAlert({
              type: 'success',
              message: translate('already_have_an_account', { email: state?.email }),
              blockAutoDismiss: true,
              showIcon: true,
            });
            history.push(`/onboarding/login`);
          }
        }
      },
    }
  );

  const handleSendEmail = async (e) => {
    if (e) e.preventDefault();
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_EMAIL);
      if (!token) {
        return false;
      }
      sendEmailVerification({ email: state.email, token, country_id: state.countryOfResidenceId });
    } catch (error) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!state?.email) {
      history.push('/onboarding/signup');
    }
    if (state?.verification_id) {
      setVerificationId(state?.verification_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    emailCountdown.bind(setTimer);
    emailCountdown.start();
    return () => {
      emailCountdown.stop();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitRequest(true);

    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.SIGNUP_VERIFY_EMAIL);
      if (!token) {
        return false;
      }

      verifyEmailByCode({ ...form, verificationId, token });
    } catch (error) {
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    updateForm({ ...form, [name]: value });
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
  };

  const renderButtons = () => (
    <>
      <div className="d-none d-md-block w-100">
        <button
          className="btn btn-primary w-100 py-3 text-bold"
          type="submit"
          loading={isLoadingVerifyEmail ? 'true' : 'false'}
          disabled={form.code === '' || isLoadingVerifyEmail}
          form="email-confirmation">
          {translate('confirm_button_label')}
        </button>
      </div>
      <div className="d-block d-md-none w-100 position-fixed bottom-0 bg-lighter py-3 px-3">
        <button
          className="btn btn-primary w-100 py-3 text-bold"
          type="submit"
          loading={isLoadingVerifyEmail ? 'true' : 'false'}
          disabled={form.code === '' || isLoadingVerifyEmail}
          form="email-confirmation">
          {translate('confirm_button_label')}
        </button>
      </div>
    </>
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    if (isSubmitRequest) {
      verifyEmailByCode({ ...form, verificationId, token: tokenV2 });
    } else {
      sendEmailVerification({
        email: state.email,
        token: tokenV2,
        country_id: state.countryOfResidenceId,
      });
    }
  };

  return (
    <>
      <ConfirmEmailComponent
        form={form}
        timer={timer}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        handleSendEmail={handleSendEmail}
        clearError={clearError}
        renderButtons={renderButtons}
        isLoading={isLoading}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading || isLoadingVerifyEmail}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default ConfirmEmail;
